import { useField } from "formik";
import { Autocomplete } from "@mui/material";
import {
  AutocompleteRenderInputParams,
  debounce,
  ListItem,
  ListItemText,
  TextField,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { ApiError, Benutzer, BenutzerListe } from "../../api";
import { useGetBenutzer, useGetAngestellte } from "../../client/hooks";

interface BauleiterSelectProps {
  name: string;
  label: string;
  required?: boolean;
  fullWidth?: boolean;
}

function BauleiterSelect({
  name,
  label,
  required,
  fullWidth,
}: BauleiterSelectProps) {
  const [field, meta, helpers] = useField(name);
  const getAngestellte = useGetAngestellte();
  const ladeBenutzer = useGetBenutzer();
  const [optionen, setOptionen] = useState<ReadonlyArray<Benutzer>>([]);
  const [searchValue, setSearchValue] = useState("");

  const value = useMemo(() => {
    return field.value || null;
  }, [field.value]);

  useEffect(() => {
    if (field.value) {
      if (typeof field.value === "string") {
        ladeBenutzer(field.value)
          .then((benutzer) => {
            helpers.setValue(benutzer);
          })
          .catch((error) => {
            console.error("Error loading user:", error);
          });
      } else if (
        field.value.id &&
        (!field.value.vorname || field.value.personalNummer === undefined)
      ) {
        // Falls wir nur ein Rumpf-Objekt haben (z.B. aus BaustellenDetails), laden wir den vollen Benutzer
        ladeBenutzer(field.value.id)
          .then((benutzer) => {
            if (
              benutzer.id !== field.value?.id ||
              benutzer.personalNummer !== field.value?.personalNummer
            ) {
              helpers.setValue(benutzer);
            }
          })
          .catch((error) => {
            console.error("Error loading user details:", error);
          });
      }
    }
  }, [field.value?.id, field.value, ladeBenutzer, helpers]);

  const ladeOptionen = useMemo(
    () =>
      debounce(
        async (
          request: { input: string },
          callback: (error?: ApiError, results?: BenutzerListe) => void
        ) =>
          getAngestellte({
            search: request.input,
            page: 0,
            limit: 20,
            sort: "name",
          })
            .then(async (results) => {
              callback(undefined, results);
            })
            .catch((error) => callback(error)),
        400
      ),
    [getAngestellte]
  );

  useEffect(() => {
    let active = true;
    ladeOptionen({ input: searchValue }, (error, results) => {
      if (!active) return;
      if (error) console.error("Error:", error);
      setOptionen([...(results ? results.items : [])]);
    });
    return () => {
      active = false;
      ladeOptionen.clear();
    };
  }, [searchValue, ladeOptionen]);

  return (
    <Autocomplete
      id={name}
      value={value}
      isOptionEqualToValue={(option, value) => {
        if (typeof option === "string" || typeof value === "string") {
          return option === value;
        }
        return option.id === value.id;
      }}
      filterSelectedOptions={true}
      filterOptions={(x) => x}
      options={optionen}
      fullWidth={fullWidth}
      getOptionLabel={(option: string | Benutzer) => {
        if (typeof option === "string") {
          return option;
        }
        if (option.vorname || option.personalNummer) {
          const nameParts = [option.name, option.vorname].filter(Boolean);
          const fullName = nameParts.join(", ");
          return option.personalNummer
            ? `${fullName} - ${option.personalNummer}`
            : fullName;
        }
        return option.name || "";
      }}
      onChange={(event, newValue) => {
        helpers.setValue(newValue);
      }}
      onBlur={() => {
        helpers.setTouched(true);
      }}
      onInputChange={(event, newInputValue) => {
        setSearchValue(newInputValue);
      }}
      renderOption={(props, option: string | Benutzer) => {
        const { key, ...optionProps } = props as any;
        if (typeof option === "string") {
          return (
            <ListItem key={key} {...optionProps}>
              {option}
            </ListItem>
          );
        }
        return (
          <ListItem key={option.id} {...optionProps}>
            <ListItemText
              primary={[option.name, option.vorname].filter(Boolean).join(", ")}
              secondary={option.personalNummer}
            />
          </ListItem>
        );
      }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          name={name}
          label={label}
          required={required}
          error={meta.touched && !!meta.error}
          helperText={meta.touched && meta.error}
          inputProps={{
            ...params.inputProps,
            "aria-label": label,
          }}
        />
      )}
    />
  );
}

export default BauleiterSelect;
